import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Switch, FormControlLabel, TextField, Button, Alert, CircularProgress, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Select, MenuItem, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Tooltip
} from '@mui/material';
import { ArrowUpward, ArrowDownward, Delete, Edit, Add } from '@mui/icons-material';
import axios from 'axios';
import { API, getAuthHeader } from './customerApp/shared';

const SERVICE_LABELS = {
  any: 'Any service', jump: 'Battery jump', lockout: 'Lockout', fuel: 'Fuel delivery', tire: 'Tire change', tow: 'Tow', winch: 'Winch out'
};

const blankItem = () => ({ id: '', serviceKey: 'any', title: '', blurb: '', imageUrl: '', url: '', priceText: '', enabled: true });

function ItemDialog({ open, item, serviceKeys, onClose, onSave }) {
  const [form, setForm] = useState(item || blankItem());
  useEffect(() => { setForm(item || blankItem()); }, [item, open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{item && item.title ? 'Edit product' : 'Add product'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={7}><TextField size="small" fullWidth label="Title" value={form.title} onChange={(e) => set('title', e.target.value)} /></Grid>
          <Grid item xs={12} sm={5}>
            <Select size="small" fullWidth value={form.serviceKey} onChange={(e) => set('serviceKey', e.target.value)}>
              {serviceKeys.map((k) => <MenuItem key={k} value={k}>{SERVICE_LABELS[k] || k}</MenuItem>)}
            </Select>
          </Grid>
          <Grid item xs={12}><TextField size="small" fullWidth label="Short description" value={form.blurb} onChange={(e) => set('blurb', e.target.value)} /></Grid>
          <Grid item xs={12}><TextField size="small" fullWidth label="Product link" placeholder="https://www.amazon.com/dp/…" value={form.url} onChange={(e) => set('url', e.target.value)} helperText="Amazon links get your Associates tag added automatically" /></Grid>
          <Grid item xs={12} sm={8}><TextField size="small" fullWidth label="Image URL (optional)" value={form.imageUrl} onChange={(e) => set('imageUrl', e.target.value)} /></Grid>
          <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Price text (optional)" placeholder="$59.99" value={form.priceText} onChange={(e) => set('priceText', e.target.value)} /></Grid>
        </Grid>
        {(form.title || form.blurb) && (
          <Paper variant="outlined" sx={{ p: 2, mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            {form.imageUrl && <Box component="img" src={form.imageUrl} alt="" sx={{ width: 64, height: 64, objectFit: 'contain' }} />}
            <Box>
              <Typography variant="subtitle2">{form.title}</Typography>
              <Typography variant="body2" color="text.secondary">{form.blurb}</Typography>
              {form.priceText && <Typography variant="body2">{form.priceText}</Typography>}
            </Box>
          </Paper>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!form.title || !form.url} onClick={() => onSave(form)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AdsOffers() {
  const [config, setConfig] = useState(null);
  const [serviceKeys, setServiceKeys] = useState(Object.keys(SERVICE_LABELS));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(null); // { index, item } | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/ads-admin/config`, { headers: getAuthHeader() });
      setConfig({ adsense: data.adsense, productCards: data.productCards });
      if (Array.isArray(data.serviceKeys)) setServiceKeys(data.serviceKeys);
      setError('');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load ad settings');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setAdsense = (k, v) => setConfig((c) => ({ ...c, adsense: { ...c.adsense, [k]: v } }));
  const setSlot = (k, v) => setConfig((c) => ({ ...c, adsense: { ...c.adsense, blogSlots: { ...(c.adsense.blogSlots || {}), [k]: v } } }));
  const setCards = (k, v) => setConfig((c) => ({ ...c, productCards: { ...c.productCards, [k]: v } }));
  const items = config?.productCards?.items || [];
  const setItems = (next) => setCards('items', next.map((it, i) => ({ ...it, sortOrder: i + 1 })));

  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
  };

  const saveItem = (form) => {
    const next = [...items];
    if (editing.index === -1) next.push(form); else next[editing.index] = form;
    setItems(next);
    setEditing(null);
  };

  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const { data } = await axios.put(`${API}/api/ads-admin/config`, config, { headers: getAuthHeader() });
      setConfig({ adsense: data.adsense, productCards: data.productCards });
      setSuccess('Saved. The website picks up changes within a few minutes.');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save ad settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  if (!config) return <Alert severity="error">{error || 'Ad settings are unavailable'}</Alert>;
  const a = config.adsense || {};
  const p = config.productCards || {};

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Box>
        <Typography variant="h6">Ads &amp; Offers</Typography>
        <Typography variant="body2" color="text.secondary">Google AdSense on the wkroadss.com blog, and &quot;Be ready next time&quot; product suggestions after a customer orders or cancels service.</Typography>
      </Box>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>Google AdSense</Typography>
          <FormControlLabel control={<Switch checked={!!a.enabled} onChange={(e) => setAdsense('enabled', e.target.checked)} />} label={a.enabled ? 'Ads on' : 'Ads off'} />
        </Box>
        <Alert severity="info" sx={{ my: 2 }}>Ads show on blog pages only — never on the homepage, ordering, careers or the assessment. Your publisher ID is published on the site for Google&apos;s verification even while ads are off.</Alert>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Publisher ID" placeholder="ca-pub-1234567890123456" value={a.publisherId || ''} onChange={(e) => setAdsense('publisherId', e.target.value.trim())} /></Grid>
          <Grid item xs={12} sm={2}><TextField size="small" fullWidth label="Top slot ID" value={a.blogSlots?.top || ''} onChange={(e) => setSlot('top', e.target.value.trim())} /></Grid>
          <Grid item xs={12} sm={2}><TextField size="small" fullWidth label="In-article slot ID" value={a.blogSlots?.inArticle || ''} onChange={(e) => setSlot('inArticle', e.target.value.trim())} /></Grid>
          <Grid item xs={12} sm={2}><TextField size="small" fullWidth label="Bottom slot ID" value={a.blogSlots?.bottom || ''} onChange={(e) => setSlot('bottom', e.target.value.trim())} /></Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Slot IDs are optional: leave them blank to let Google place Auto ads on blog pages.</Typography>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>Product cards</Typography>
          <FormControlLabel control={<Switch checked={!!p.enabled} onChange={(e) => setCards('enabled', e.target.checked)} />} label={p.enabled ? 'Shown' : 'Hidden'} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Up to three products matched to the service, shown on the website and in the customer app.</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Amazon Associates tag" placeholder="yourstore-20" value={p.amazonTag || ''} onChange={(e) => setCards('amazonTag', e.target.value.trim())} /></Grid>
          <Grid item xs={12} sm={4}><FormControlLabel control={<Switch checked={p.showAfterOrder !== false} onChange={(e) => setCards('showAfterOrder', e.target.checked)} />} label="Show after an order" /></Grid>
          <Grid item xs={12} sm={4}><FormControlLabel control={<Switch checked={p.showAfterCancel !== false} onChange={(e) => setCards('showAfterCancel', e.target.checked)} />} label="Show when a customer backs out at the price (or cancels)" /></Grid>
        </Grid>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell><TableCell>Service</TableCell><TableCell>Product</TableCell><TableCell>Price</TableCell><TableCell>On</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it, i) => (
                <TableRow key={`${it.id}-${i}`}>
                  <TableCell>
                    <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUpward fontSize="inherit" /></IconButton>
                    <IconButton size="small" disabled={i === items.length - 1} onClick={() => move(i, 1)}><ArrowDownward fontSize="inherit" /></IconButton>
                  </TableCell>
                  <TableCell><Chip size="small" label={SERVICE_LABELS[it.serviceKey] || it.serviceKey} /></TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{it.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{it.blurb}</Typography>
                  </TableCell>
                  <TableCell>{it.priceText || '—'}</TableCell>
                  <TableCell><Switch size="small" checked={it.enabled !== false} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, enabled: e.target.checked } : x)))} /></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => setEditing({ index: i, item: it })}><Edit fontSize="inherit" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" onClick={() => setItems(items.filter((_, j) => j !== i))}><Delete fontSize="inherit" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!items.length && <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">No products yet.</Typography></TableCell></TableRow>}
            </TableBody>
          </Table>
        </Box>
        <Button sx={{ mt: 1 }} startIcon={<Add />} onClick={() => setEditing({ index: -1, item: blankItem() })}>Add product</Button>
      </Paper>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        <Button onClick={load} disabled={saving}>Discard changes</Button>
      </Box>

      <ItemDialog open={!!editing} item={editing?.item} serviceKeys={serviceKeys} onClose={() => setEditing(null)} onSave={saveItem} />
    </Box>
  );
}
